import ActionsLayout from '@/components/MatchLayout'
import { useMatch } from '@/hooks/swr/useMatch'
import { post } from '@/utils/request'
import { message, Modal } from 'antd'
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Countdown from 'react-countdown'
import { cloneDeep, forEach, padStart } from 'lodash-es'
import dayjs from 'dayjs'
import useAdmin from '@/hooks/swr/useAdmin'
import { useChannel } from '@/hooks/useWebSocket'
import Button from '@/components/Button'
import { turnDataType } from '@/utils/types'
import { motion } from 'framer-motion'
import { GetServerSideProps } from 'next/types'
import { useTimerAlerts, useTimerAlertEnabled } from '@/hooks/useTimerAlerts'
import { playWarnChime, playEndChime, unlockAudio } from '@/utils/timerChime'
import TimerAlertFx, { TimerAlert } from '@/components/TimerAlertFx'
import '@pqina/flip/dist/flip.min.css'

const updateMatchTurnData = (id, turn_data) => {
  post(`matches/turn_data`, { id, turn_data })
}

export const CountDownRenderer = ({ hours, minutes, seconds, completed }, round_stop_info_time = 3) => {
  const safeHours = completed ? 0 : Math.max(0, hours)
  const safeMinutes = completed ? 0 : Math.max(0, minutes)
  const safeSeconds = completed ? 0 : Math.max(0, seconds)
  const hasHour = safeHours > 0
  const shouldHighlight = completed || (!hasHour && round_stop_info_time >= 0 && safeMinutes < round_stop_info_time)

  const className = ['font-mono', 'tracking-wide', shouldHighlight ? 'text-rose-200 drop-shadow-[0_0_25px_rgba(244,63,94,0.45)]' : ''].filter(Boolean).join(' ')

  return (
    <motion.span
      key={`${safeHours}:${safeMinutes}:${safeSeconds}:${completed ? 'completed' : 'running'}`}
      initial={{ opacity: 0.8, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={className}
    >
      {hasHour ? (
        <>
          {padStart(safeHours, 2, '0')}:{padStart(safeMinutes, 2, '0')}:{padStart(safeSeconds, 2, '0')}
        </>
      ) : (
        <>
          {padStart(safeMinutes, 2, '0')}:{padStart(safeSeconds, 2, '0')}
        </>
      )}
    </motion.span>
  )
}

type FlipTickerProps = {
  value: string
  theme?: 'default' | 'stop'
}

const FlipTicker = ({ value, theme = 'default' }: FlipTickerProps) => {
  const tickRef = useRef<HTMLDivElement | null>(null)
  const tickInstanceRef = useRef<any>(null)
  const tickLibRef = useRef<any>(null)
  const latestValueRef = useRef(value)

  useEffect(() => {
    let isMounted = true
    let tickNode: HTMLDivElement | null = null
    const loadTick = async () => {
      if (!tickLibRef.current) {
        const tickModule = await import('@pqina/flip')
        if (isMounted) {
          tickLibRef.current = tickModule.default
        }
      }
      tickNode = tickRef.current
      if (!tickLibRef.current || !tickNode || tickInstanceRef.current) return
      tickInstanceRef.current = tickLibRef.current.DOM.create(tickNode, { value: latestValueRef.current })
    }
    loadTick()
    return () => {
      isMounted = false
      if (tickInstanceRef.current && tickNode && tickLibRef.current) {
        tickLibRef.current.DOM.destroy(tickNode)
      }
      tickInstanceRef.current = null
    }
  }, [])

  useEffect(() => {
    latestValueRef.current = value
  }, [value])

  useEffect(() => {
    if (tickInstanceRef.current) {
      tickInstanceRef.current.value = value
    }
  }, [value])

  return (
    <div className={`mx-auto w-full max-w-[640px] px-2 text-center sm:max-w-[780px] lg:max-w-[920px] ${theme === 'stop' ? 'flip-stop-mode' : ''}`}>
      <div ref={tickRef} className="tick flip-clock justify-center">
        <div data-repeat="true" aria-hidden="true">
          <span data-view="flip">Tick</span>
        </div>
      </div>
    </div>
  )
}

type FlipRendererOptions = {
  compact?: boolean
}

export const FlipCountDownRenderer = ({ hours, minutes, seconds, completed }, round_stop_info_time = 3, options: FlipRendererOptions = {}) => {
  const { compact = false } = options
  const safeHours = completed ? 0 : Math.max(0, hours)
  const safeMinutes = completed ? 0 : Math.max(0, minutes)
  const safeSeconds = completed ? 0 : Math.max(0, seconds)
  const hasHour = safeHours > 0
  const stopWindowActive = round_stop_info_time >= 0 && !hasHour && safeMinutes < round_stop_info_time
  const highlight = completed || stopWindowActive

  const formattedValue = hasHour
    ? `${padStart(safeHours, 2, '0')}:${padStart(safeMinutes, 2, '0')}:${padStart(safeSeconds, 2, '0')}`
    : `${padStart(safeMinutes, 2, '0')}:${padStart(safeSeconds, 2, '0')}`
  const displayValue = completed ? (hasHour ? '00:00:00' : '00:00') : formattedValue
  const ticker = <FlipTicker value={displayValue} theme={highlight ? 'stop' : 'default'} />

  if (compact) {
    return <div className="scale-[0.94] transform-gpu">{ticker}</div>
  }

  return ticker
}

export const useTurnDate = (match) => {
  const turnData = useMemo(() => {
    if (!match.turn) return []
    const turnList: turnDataType[] = [...Array(match.turn)].map((__, index) => ({ turn: index + 1 } as turnDataType)) as any
    forEach(match.turn_data, (td, index) => {
      if (index + 1 > turnList.length) return
      turnList[index] = td
    })
    return turnList
  }, [match.turn, match.turn_data])

  const turn = useMemo(() => match.timer_turn || 0, [match.timer_turn])

  const date = useMemo(() => {
    if (turn % 1 === 0) return 0
    return turnData[turn - 0.5].start_time + match?.time * 60 * 1000 || 3000000
  }, [match?.time, turn, turnData])

  return { turnData, turn, date }
}

const Timer = ({ match_id }) => {
  const { data: match, mutate } = useMatch(match_id)
  const { isAdmin } = useAdmin(match.id)

  const [turnCompleted, setTurnCompleted] = useState<boolean>(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [useFlipCountdown, setUseFlipCountdown] = useState<boolean>(true)

  const { turnData, turn, date } = useTurnDate(match)
  const stopInfoTime = useMemo(() => match?.round_stop_info_time || 3, [match?.round_stop_info_time])
  const isStopPhase = useMemo(() => {
    if (!date || date <= 0) return false
    if (date <= Date.now()) return false
    return date - Date.now() <= stopInfoTime * 60 * 1000
  }, [date, stopInfoTime])
  const roundLabel = useMemo(() => {
    if (!turn) return '比赛尚未开始'
    if (isStopPhase) return `第${Math.ceil(turn)}轮 · 停止抓牌`
    if (turn % 1 === 0) return `第${turn}轮准备中`
    return `第${Math.ceil(turn)}轮进行中`
  }, [turn, isStopPhase])
  const roundLabelClass = isStopPhase ? 'text-rose-200' : 'text-white'
  const canStartNextTurn = useMemo(() => turn % 1 === 0 && turn < turnData.length, [turn, turnData.length])
  const runningTurnIndex = useMemo(() => (turn % 1 !== 0 ? Math.floor(turn) : null), [turn])

  const [alert, setAlert] = useState<TimerAlert>(null)
  const alertIdRef = useRef(0)
  const [alertEnabled, setAlertEnabled] = useTimerAlertEnabled()
  const triggerAlert = (type: 'stop' | 'end') => {
    alertIdRef.current += 1
    setAlert({ type, id: alertIdRef.current })
  }
  useTimerAlerts(date, stopInfoTime * 60 * 1000, alertEnabled, {
    onStop: () => {
      playWarnChime()
      triggerAlert('stop')
    },
    onEnd: () => {
      playEndChime()
      triggerAlert('end')
    },
  })

  const time_interver_error = useMemo(() => {
    if (match?.server_time) {
      const server_time = match?.server_time
      const client_time = Math.round(Date.now() / 1000)
      const time_interval = Math.abs(server_time - client_time)
      if (time_interval > 300) {
        return true
      }
    }
    return false
  }, [match?.server_time])

  const changeTurn = useCallback(
    (index, type) => {
      if (!isAdmin) {
        message.error('您不是管理员,若有疑问,请联系比赛主办方')
        return
      }
      unlockAudio() // 借这次点击手势解锁音频,后续到点铃声才能自动播放
      setTurnCompleted(false)
      const newDateUnix = Date.now()
      const newTurnData = cloneDeep(turnData)
      if (type === 'start') {
        newTurnData[index] = { ...newTurnData[index], start_time: newDateUnix }
      } else if (type === 'restart') {
        newTurnData[index] = { ...newTurnData[index], start_time: undefined }
      } else {
        newTurnData[index] = { ...newTurnData[index], end_time: newDateUnix }
      }
      mutate((cachedMatch) => ({ ...cachedMatch, turn_data: newTurnData }), false)

      updateMatchTurnData(match_id, newTurnData)
    },
    [isAdmin, match_id, mutate, turnData]
  )

  useEffect(() => {
    if (turnCompleted && isAdmin) {
      changeTurn(turn - 0.5, 'end')
    }
  }, [turnCompleted, isAdmin, changeTurn, turn])

  const timerActions = (index) => {
    if (turn > index + 0.5) {
      return (
        <Button disabled className="rounded-full border border-white/20 bg-white/5 px-5 text-sm text-white/60 backdrop-blur">
          已结束
        </Button>
      )
    }
    if (turn === index + 0.5) {
      return (
        <div className="flex items-center justify-end gap-2">
          <Button
            onClick={() => changeTurn(index, 'restart')}
            className="rounded-full border border-cyan-300/60 bg-cyan-500/10 px-5 text-xs font-semibold text-cyan-100 shadow-[inset_0_0_20px_rgba(6,182,212,0.25)]"
          >
            重新计时
          </Button>
          <Button
            onClick={() => changeTurn(index, 'end')}
            type="primary"
            danger
            ghost
            className="rounded-full border border-rose-300/70 bg-rose-500/10 px-5 text-xs font-semibold text-rose-100 shadow-[inset_0_0_25px_rgba(244,63,94,0.35)]"
          >
            终止
          </Button>
        </div>
      )
    }
    if (turn === index) {
      return (
        <Button
          type="primary"
          ghost
          onClick={() => changeTurn(index, 'start')}
          className="rounded-full border border-emerald-300/70 bg-emerald-500/10 px-6 text-sm font-semibold text-emerald-100 shadow-[0_10px_40px_rgba(16,185,129,0.35)]"
        >
          开始
        </Button>
      )
    }
    return (
      <Button disabled className="rounded-full border border-white/20 bg-white/5 px-5 text-sm text-white/70">
        未开始
      </Button>
    )
  }

  const handleGlobalStart = () => {
    if (!canStartNextTurn) return
    changeTurn(turn, 'start')
  }

  const handleGlobalStop = () => {
    if (runningTurnIndex === null) return
    changeTurn(runningTurnIndex, 'end')
  }

  useChannel(`match-${match.id}`, true, (obj) => {
    switch (obj.action) {
      case 'updateTurn':
        mutate(() => obj.data.match, false)
        break
      default:
    }
  })

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950">
      <TimerAlertFx alert={alert} />
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-10 top-10 h-72 w-72 rounded-full bg-indigo-600/30 blur-[140px]" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-rose-500/20 blur-[170px]" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-10 lg:px-0"
      >
        <div className="rounded-[2.5rem] border border-white/10 bg-white/5 p-8 text-white shadow-[0_35px_120px_rgba(15,23,42,0.65)] backdrop-blur-xl">
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.6em] text-white/50">Match Timer</p>
                <h1 className={`mt-3 text-3xl font-semibold ${roundLabelClass}`}>{roundLabel}</h1>
                <p className="text-sm text-white/60">单轮时长 {match?.time ?? '--'} 分钟</p>
              </div>
              <div className="flex flex-wrap items-center gap-3 rounded-full border border-white/10 bg-black/30 px-3 py-2 text-xs font-semibold text-white/70 shadow-[0_15px_35px_rgba(15,23,42,0.35)]">
                <span className="text-white/70">显示模式</span>
                <div className="flex overflow-hidden rounded-full border border-white/10 bg-white/80 text-xs">
                  <button
                    type="button"
                    onClick={() => setUseFlipCountdown(false)}
                    className={`px-4 py-1 transition-colors ${!useFlipCountdown ? 'bg-slate-900 text-white shadow-inner shadow-slate-900/40' : 'text-slate-900/70'}`}
                  >
                    简洁
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseFlipCountdown(true)}
                    className={`px-4 py-1 transition-colors ${useFlipCountdown ? 'bg-slate-900 text-white shadow-inner shadow-slate-900/40' : 'text-slate-900/70'}`}
                  >
                    灵动
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    unlockAudio()
                    setAlertEnabled(!alertEnabled)
                  }}
                  className={`ml-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                    alertEnabled ? 'bg-emerald-500/20 text-emerald-100' : 'bg-white/10 text-white/50'
                  }`}
                  title="计时进入停止抓牌和归零时,播放铃声+屏幕特效"
                >
                  {alertEnabled ? '🔔 到点提示 开' : '🔕 到点提示 关'}
                </button>
              </div>
            </div>
            <div className="relative mx-auto w-full rounded-[2rem] border border-white/10 bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-indigo-900/80 px-6 py-6 text-center shadow-[0_35px_90px_rgba(6,13,34,0.55)]">
              <div
                className={`flex min-h-[150px] w-full flex-col items-center justify-center ${
                  useFlipCountdown ? '' : 'border-white/15 rounded-[1.5rem] border bg-white/5 px-6 py-4 shadow-[0_25px_60px_rgba(6,13,34,0.45)]'
                }`}
              >
                {(() => {
                  if (date > Date.now()) {
                    return (
                      <Countdown
                        date={date}
                        renderer={(props) =>
                          useFlipCountdown ? (
                            FlipCountDownRenderer(props, match.round_stop_info_time)
                          ) : (
                            <div className="text-6xl font-light tracking-[0.3em] text-white drop-shadow-2xl md:text-7xl">
                              {CountDownRenderer(props, match.round_stop_info_time)}
                            </div>
                          )
                        }
                      />
                    )
                  }
                  if (date > 0) {
                    return <div className="text-4xl font-semibold tracking-[0.25em] text-white drop-shadow-lg md:text-5xl">计时结束</div>
                  }
                  return <div className="text-4xl font-semibold text-white/80 drop-shadow-md md:text-5xl">等待开始</div>
                })()}
              </div>
            </div>
          </div>
        </div>

        {time_interver_error && (
          <div className="rounded-2xl border border-amber-300/50 bg-amber-500/10 p-4 text-center text-amber-100 shadow-lg shadow-amber-900/30 backdrop-blur">
            <div>您的时间和灵缇系统时间相差 5 分钟以上</div>
            <div className="text-sm text-amber-200/80">如果使用多台设备联机显示时间，请校准电脑系统时间</div>
          </div>
        )}

        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-white shadow-[0_25px_80px_rgba(15,23,42,0.45)] backdrop-blur-xl lg:p-8">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-2 text-sm text-white/70">
            <h2 className="text-2xl font-semibold text-white">轮次控制</h2>
            <p className="text-sm text-white/60">共 {turnData.length} 轮 · 实时同步到所有设备</p>
          </div>
          <div className="space-y-4">
            {turnData.map((v, index) => (
              <motion.div
                key={v.turn}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onHoverStart={() => setActiveIndex(index)}
                onHoverEnd={() => setActiveIndex(null)}
                className={`rounded-2xl border px-4 py-4 transition-all duration-300 lg:px-6 ${
                  activeIndex === index ? 'border-white/40 bg-white/10 shadow-[0_15px_45px_rgba(15,23,42,0.45)]' : 'border-white/10 bg-white/5'
                }`}
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500/60 to-purple-600/60 text-xl font-semibold text-white shadow-lg shadow-indigo-900/40">
                      {v.turn}
                    </div>
                    <div>
                      <div className="text-sm uppercase tracking-[0.2em] text-white/60">第 {v.turn} 轮</div>
                      <div className="mt-2 grid grid-cols-2 gap-4 text-xs text-white/70">
                        <div>
                          开始时间 <span className="font-semibold text-white">{v.start_time ? dayjs(v.start_time).format('HH:mm:ss') : '—'}</span>
                        </div>
                        <div>
                          结束时间 <span className="font-semibold text-white">{v.end_time ? dayjs(v.end_time).format('HH:mm:ss') : '—'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end lg:min-w-[220px]">{timerActions(index)}</div>
                </div>
              </motion.div>
            ))}
          </div>

          {isAdmin && (
            <div className="mt-10 flex flex-col gap-3">
              <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4">
                <div className="flex flex-1 items-center justify-center gap-3">
                  <Button
                    type="primary"
                    disabled={!canStartNextTurn}
                    onClick={handleGlobalStart}
                    className="h-11 min-w-[140px] rounded-2xl border-none bg-gradient-to-r from-[#e5455a] to-[#c02233] px-8 text-base font-semibold text-white shadow-[0_20px_45px_rgba(219,49,66,0.35)] disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/50"
                  >
                    开始计时
                  </Button>
                  {runningTurnIndex !== null && (
                    <Button
                      danger
                      type="primary"
                      onClick={handleGlobalStop}
                      className="h-11 min-w-[140px] rounded-2xl border-none bg-gradient-to-r from-rose-500 to-orange-500 px-8 text-base font-semibold text-white shadow-[0_20px_45px_rgba(244,63,94,0.35)]"
                    >
                      结束本轮计时
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-4 flex justify-center">
                <Button
                  size="small"
                  ghost
                  className="h-11 rounded-full border border-white/20 px-6 text-xs text-white/80"
                  onClick={() => {
                    Modal.confirm({
                      title: '确认重置',
                      content: '您确定要全部重置时间吗？此操作不可撤销。',
                      okText: '确认重置',
                      cancelText: '取消',
                      okButtonProps: {
                        danger: true,
                      },
                      onOk: () => {
                        updateMatchTurnData(match_id, [])
                      },
                    })
                  }}
                >
                  重置所有时间
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  return {
    props: {
      match_id: ctx.query.match_id,
    },
  }
}

Timer.getLayout = function getLayout(children: ReactElement) {
  return <ActionsLayout>{children}</ActionsLayout>
}

export default Timer
